import { useNavigate } from 'react-router-dom'
import { Disc, BarChart3, Check, Circle, Users, Trophy, Target, Zap, Crown, ArrowRight, Star } from 'lucide-react'
import { formatHumanDate } from '../lib/dateUtils'
import { allBracketsComplete } from '../lib/tournamentSort'
import { tournamentStatusVariant } from '../lib/badgeVariants'
import { track } from '../lib/analytics'
import { compareDivisions } from '../lib/divisionOrder'
import { Badge } from './ui/badge'
import CountdownTimer from './CountdownTimer'

/**
 * TournamentCard - A reusable component that displays a tournament with nested bracket items.
 *
 * variant="full" (default): rich card with brackets, countdowns, metadata.
 * variant="compact": single-row summary card for browse/list views.
 *
 * @param {string} variant - "full" | "compact"
 * @param {boolean} isFavorite - Whether the tournament is favorited (compact variant)
 * @param {Function} onToggleFavorite - Callback to toggle favorite (compact variant)
 */
export default function TournamentCard({
  tournament,
  onBracketClick,
  hideHeader = false,
  isViewingOtherUser = false,
  variant = 'full',
  isFavorite = false,
  onToggleFavorite,
  isAdminContext = false,
}) {
  const navigate = useNavigate()
  const tournamentHref = isAdminContext
    ? `/admin/tournaments/${tournament.id}/edit`
    : `/tournaments/${tournament.id}`

  // Check if tournament is completed (past end date)
  const isCompleted = () => {
    if (!tournament.end_date) return false
    const endDate = new Date(tournament.end_date)
    const now = new Date()
    return now > endDate
  }

  // Check if tournament is upcoming (published but start date in future)
  const isUpcoming = () => {
    if (!tournament.start_date || tournament.status !== 'published') return false
    const startDate = new Date(tournament.start_date)
    const now = new Date()
    return now < startDate
  }

  const tournamentCompleted = isCompleted()
  const tournamentUpcoming = isUpcoming()

  const handleBracketClick = (bracket) => {
    track('bracket_opened', { bracketId: bracket.id, tournamentId: tournament.id, hasEntry: !!bracket.user_entry_id })
    if (onBracketClick) {
      onBracketClick(bracket)
    } else {
      // Default behavior: navigate to bracket picks
      if (bracket.user_entry_id) {
        navigate(`/bracket/${bracket.id}/picks?entryId=${bracket.user_entry_id}`)
      } else {
        navigate(`/bracket/${bracket.id}/picks`)
      }
    }
  }

  // Derive the display status key used for both label and variant
  const getEffectiveStatus = (status) => {
    if (tournamentUpcoming) return 'upcoming'
    if (tournamentCompleted) return 'locked'
    return status
  }

  const statusLabels = {
    upcoming: 'Upcoming',
    open: 'Open for picks',
    locked: 'Closed for picks',
    draft: 'Draft',
    complete: 'Complete',
  }

  const getStatusLabel = (status) => {
    const effective = getEffectiveStatus(status)
    return statusLabels[effective] || status
  }

  const getStatusVariant = (status) => {
    const effective = getEffectiveStatus(status)
    return tournamentStatusVariant[effective] || 'muted'
  }

  // Determine if user has any entries for this tournament
  const hasAnyEntries = tournament.brackets?.some(bracket => bracket.user_entry_id)

  // Find active submission deadline across brackets/rounds or tournament level
  const getActiveDeadline = () => {
    if (Array.isArray(tournament.brackets)) {
      for (const bracket of tournament.brackets) {
        if (Array.isArray(bracket.rounds)) {
          const openRound = bracket.rounds.find(r => r.status === 'open')
          if (openRound && openRound.submission_deadline) {
            return openRound.submission_deadline
          }
        }
      }
    }
    return tournament.submission_deadline || tournament.submissionDeadlineFull || null
  }

  const activeDeadline = getActiveDeadline()
  const hasActiveOpenRound = Array.isArray(tournament.brackets) && tournament.brackets.some(b => b.rounds?.some(r => r.status === 'open'))
  const showTimer = (tournament.status === 'open' || hasActiveOpenRound) && activeDeadline && new Date() < new Date(activeDeadline)

  // Show metadata when user has entries OR tournament is locked/complete
  const showMetadata = hasAnyEntries || tournament.status === 'locked' || tournament.status === 'complete'

  const getCompactTimeStatus = () => {
    // DUB-184: all official results entered → the tournament is over, even
    // if its end_date hasn't passed yet.
    if (allBracketsComplete(tournament)) return 'past'
    const endDate = tournament.end_date || tournament.endDate
    const startDate = tournament.start_date || tournament.startDate
    if (endDate) {
      const end = new Date(endDate)
      if (new Date() > end) return 'past'
    }
    if (startDate) {
      const start = new Date(startDate)
      if (new Date() < start) return 'upcoming'
    }
    return 'live'
  }

  if (variant === 'compact') {
    const timeStatus = getCompactTimeStatus()
    const statusLabel = timeStatus === 'live' ? 'Live' : timeStatus === 'upcoming' ? 'Upcoming' : 'Past'
    const bracketCount = tournament.divisions ?? tournament.brackets?.length ?? 0
    const isTest = !!(tournament.isTest ?? tournament.is_test)

    return (
      <div
        className="card-glass-item p-4 sm:p-5 rounded-lg cursor-pointer transition-all hover:shadow-md"
        onClick={() => navigate(tournamentHref)}
      >
        <div className="flex items-center gap-3 sm:gap-4">
          {onToggleFavorite && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(tournament.id, e) }}
              className="flex-shrink-0 hover:scale-110 transition-transform"
            >
              <Star
                className={`h-5 w-5 ${
                  isFavorite
                    ? 'fill-[var(--orange-9)] text-[var(--orange-9)]'
                    : 'text-muted-foreground hover:text-[var(--orange-9)]'
                }`}
              />
            </button>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
                {tournament.sport_name || tournament.sportName}
              </span>
              <Badge variant={tournamentStatusVariant[timeStatus] || 'muted'} className="text-[10px] px-1.5 py-0">
                {statusLabel}
              </Badge>
              {isTest && (
                <Badge variant="muted" className="text-[10px] px-1.5 py-0 uppercase tracking-wide">
                  Test
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-base">{tournament.name || tournament.title}</h3>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span>{formatHumanDate(tournament.start_date || tournament.startDate)}</span>
              <span className="text-muted-foreground/50">·</span>
              <span>{bracketCount} {bracketCount === 1 ? 'bracket' : 'brackets'}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card-glass-item p-4 sm:p-6 rounded-lg">
      {/* Tournament Header */}
      {!hideHeader && (
        <div className="mb-6">
        {/* Countdown Timer or Metadata Row */}
        <div className="flex items-center justify-between mb-9">
          {/* Left side: Timer, Metadata, or Event Completed */}
          {tournamentCompleted ? (
            <span className="text-sm font-medium text-muted-foreground">
              Event completed
            </span>
          ) : tournamentUpcoming ? (
            <CountdownTimer 
              targetDate={tournament.start_date}
              showSeconds={false}
              prefix="Event starts in"
              className="text-sm"
            />
          ) : showTimer && !showMetadata ? (
            <CountdownTimer 
              targetDate={activeDeadline}
              showSeconds={true}
              prefix="Brackets close in"
              className="text-sm"
            />
          ) : showMetadata && tournament.metadata ? (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Badge variant="muted" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                Global
              </Badge>
              <span className="flex items-center gap-1">
                <Trophy className="h-3.5 w-3.5 text-[var(--teal-9)]" />
                Points <span className="font-medium text-foreground">{tournament.metadata.total_points || 0}</span>
              </span>
              <span className="flex items-center gap-1">
                <Crown className={`h-3.5 w-3.5 ${
                  tournament.metadata.champion_status === 'eliminated'
                    ? 'text-[var(--color-pick-wrong-text)]'
                    : tournament.metadata.champion_status === 'won'
                    ? 'text-[var(--color-success)]'
                    : 'text-[var(--color-crown)]'
                }`} />
                Champions Picked <span className="font-medium text-foreground">{tournament.metadata.champions_picked || 0}</span>
              </span>
            </div>
          ) : showTimer && showMetadata ? (
            <CountdownTimer 
              targetDate={activeDeadline}
              showSeconds={true}
              prefix="Brackets close in"
              className="text-sm"
            />
          ) : null}

          {/* Right side: Leaderboard link + Status Badge */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                const params = new URLSearchParams()
                if (tournament.sport_id) params.set('sport', tournament.sport_id)
                if (tournament.id) params.set('tournament', tournament.id)
                navigate(`/leaderboards?${params.toString()}`)
              }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              aria-label="Open tournament leaderboard"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Leaderboard
            </button>
            {(tournament.isTest ?? tournament.is_test) && (
              <Badge variant="muted" className="uppercase tracking-wide text-[10px]">
                Test
              </Badge>
            )}
            <Badge variant={getStatusVariant(tournament.status)}>
              {getStatusLabel(tournament.status)}
            </Badge>
          </div>
        </div>

        {/* Sport Name */}
        <div className="mb-3">
          <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
            {tournament.sport_name}
          </span>
        </div>

        {/* Tournament Name */}
        <h2
          className="text-2xl font-bold mb-2 cursor-pointer hover:text-primary transition-colors"
          onClick={() => navigate(tournamentHref)}
        >
          {tournament.name}
        </h2>

        {/* Date Range */}
        <p className="text-sm text-muted-foreground">
          {formatHumanDate(tournament.start_date)} - {formatHumanDate(tournament.end_date)}
        </p>
      </div>
      )}

      {/* Bracket List */}
      <div className="space-y-2">
        {tournament.brackets && tournament.brackets.length > 0 ? (
          [...tournament.brackets]
            .sort(compareDivisions)
            .filter(bracket => !isViewingOtherUser || bracket.user_entry_id)
            .map((bracket) => (
            <div
              key={bracket.id}
              onClick={() => handleBracketClick(bracket)}
              className="card-glass-item p-4 rounded-lg transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {/* Entry Status Indicator - hide for completed tournaments */}
                  {!tournamentCompleted && (
                    (bracket.user_entry_id || isViewingOtherUser) ? (
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--teal-9)] flex items-center justify-center">
                        <Check className="h-3 w-3 text-[var(--color-white)]" />
                      </div>
                    ) : (
                      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )
                  )}
                  
                  {/* Division Name */}
                  <h3 className="font-semibold">{bracket.division_name}</h3>
                </div>

                {/* Action Button */}
                <button
                  className="px-3 py-1.5 text-sm font-medium rounded-full bg-muted hover:bg-muted/80 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleBracketClick(bracket)
                  }}
                >
                  {tournamentCompleted ? 'View Bracket' : tournamentUpcoming ? 'View Bracket' : isViewingOtherUser ? 'View Picks' : bracket.user_entry_id ? 'View Picks' : 'Make Picks'}
                </button>
              </div>

              {/* Bracket-level Metadata */}
              {bracket.metadata && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Trophy className="h-3.5 w-3.5 text-[var(--teal-9)]" />
                    Points <span className="font-medium text-foreground">{bracket.metadata.total_points || 0}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Crown className={`h-3.5 w-3.5 ${
                      bracket.metadata.champion_status === 'eliminated'
                        ? 'text-[var(--color-pick-wrong-text)]'
                        : bracket.metadata.champion_status === 'won'
                        ? 'text-[var(--color-success)]'
                        : 'text-[var(--color-crown)]'
                    }`} />
                    Champion <span className={`font-medium ${
                      bracket.metadata.champion_status === 'eliminated'
                        ? 'text-[var(--color-pick-wrong-text)]'
                        : bracket.metadata.champion_status === 'won'
                        ? 'text-[var(--color-success)]'
                        : ''
                    }`}>{bracket.metadata.champion_status === 'active'
                      ? 'Active'
                      : bracket.metadata.champion_status === 'won'
                      ? 'Won'
                      : bracket.metadata.champion_status === 'eliminated'
                      ? 'Eliminated'
                      : 'Not Selected'}</span>
                  </span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="py-4">
            <button
              onClick={() => navigate(tournamentHref)}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {isAdminContext ? 'Manage Tournament' : 'View Event'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
